import { Grid, Autocomplete, TextField, Chip, Box, Typography } from "@mui/material"
import { Move, MOVE_DAMAGE_CLASS_PHYSICAL } from "../../../move/move"
import { PokemonV2 } from "../../../pokemon/pokemon"
import { AttackPokemonData } from "../../../types/attack"
import { getSortedMovesForAllSelect, getSortedMovesForSelect } from "../../../lib/move-rules"
import { formatPokemonDisplayName } from "../../../utils/pokemon-display-name"
import { normalizeForSearch } from "../../../utils/search"

interface SelectMoveProps {
  selectedAttackPokemonForMove: string | null
  attackPokemonList: AttackPokemonData[]
  getAllAttackMoves: (pokemon: PokemonV2) => Move[]
  handleSelectMoveForAttackPokemon: (id: string, moveId: number) => void
  setSelectedAttackPokemonForMove: (a: string | null) => void
  getMoveDisplayName: (move: Move, pokemonId: number) => string
  getMoveDisplayPower: (move: Move, pokemonId: number) => number | string
}

function SelectMove({
  selectedAttackPokemonForMove,
  attackPokemonList,
  getAllAttackMoves,
  handleSelectMoveForAttackPokemon,
  setSelectedAttackPokemonForMove,
  getMoveDisplayName,
  getMoveDisplayPower
}: SelectMoveProps) {
  if (!selectedAttackPokemonForMove || attackPokemonList.length === 0) {
    return null;
  }

  const selectedAttackPokemon = attackPokemonList.find(p => p.id === selectedAttackPokemonForMove);
  if (!selectedAttackPokemon) return null;

  // Don't show move selection if this Pokemon already has a selected move
  if (selectedAttackPokemon.selectedMoveId) return null;

  const { sortedMoves, oftenUsedAttackMoveIds } = getSortedMovesForSelect(
    selectedAttackPokemon.pokemon,
    getAllAttackMoves
  );
  // NOTE: フォーム名が重複しているケースがあるため、IDも含めて表示名を確定させる
  const selectedAttackPokemonDisplayName = formatPokemonDisplayName(selectedAttackPokemon.pokemon);

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Autocomplete
        options={sortedMoves}
        getOptionLabel={(move) => 
          `${getMoveDisplayName(move, selectedAttackPokemon.pokemon.id)} (威力: ${getMoveDisplayPower(move, selectedAttackPokemon.pokemon.id)}, ${move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ? '物理' : '特殊'})`
        }
	        renderInput={(params) => (
	          <TextField
	            {...params}
	            label={`技を選択 (${selectedAttackPokemonDisplayName})`}
	            placeholder="技名を入力して検索..."
	            variant="outlined"
	            fullWidth
	          />
	        )}
        renderOption={(props, move) => {
          const isOftenUsedAttack = oftenUsedAttackMoveIds.has(move.id);
          return (
            <Box
              component="li"
              {...props}
              sx={{
                fontWeight: isOftenUsedAttack ? 'bold' : 'normal',
                backgroundColor: isOftenUsedAttack ? '#e3f2fd' : 'transparent',
                '&:hover': {
                  backgroundColor: isOftenUsedAttack ? '#bbdefb' : '#f5f5f5'
                },
                '&[aria-selected="true"]': {
                  backgroundColor: isOftenUsedAttack ? '#bbdefb' : '#e0e0e0'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {isOftenUsedAttack && (
                  <Chip
                    label="TOP10"
                    size="small"
                    color="primary"
                    sx={{ 
                      marginRight: 1, 
                      fontSize: '0.7rem',
                      height: '20px'
                    }}
                  />
                )}
                <Typography variant="body2">
                  {getMoveDisplayName(move, selectedAttackPokemon.pokemon.id)}
                  <span style={{ color: '#666', marginLeft: '8px' }}>
                    (威力: {getMoveDisplayPower(move, selectedAttackPokemon.pokemon.id)}, 
                    {move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ? '物理' : '特殊'})
                  </span>
                </Typography>
              </Box>
            </Box>
          );
        }}
        onChange={(event, value) => {
          if (value) {
            handleSelectMoveForAttackPokemon(selectedAttackPokemon.id, value.id);
            setSelectedAttackPokemonForMove(null);
          }
        }}
        filterOptions={(options, { inputValue }) => {
          // ひらがな/カタカナの揺れを吸収して検索できるようにする（例: 「あ」で「アイアンヘッド」もヒット）
          if (!inputValue) return options;
          const normalizedInput = normalizeForSearch(inputValue);
          const filtered = options.filter((option) =>
            normalizeForSearch(getMoveDisplayName(option, selectedAttackPokemon.pokemon.id)).includes(normalizedInput)
          );
          return getSortedMovesForAllSelect(selectedAttackPokemon.pokemon, filtered).sortedMoves;
        }}
        noOptionsText="該当する技が見つかりません"
        clearOnEscape
        openOnFocus
        fullWidth
      />
    </Grid>
  );
}

export default SelectMove
